import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useDragScroll } from '@/hooks/useDragScroll';
import { cn } from '@/utils';

interface ContainerProps extends React.HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
	scrollable?: boolean;
	className?: string;
	contentClassName?: string;
	scrollTopCondition?: boolean;
	hFull?: boolean;
}

// DEPRECATED: Prefer using Card directly. This wrapper now renders a shadcn Card
// to keep legacy layouts visually consistent while you migrate usages.
export const Container = ({
	children,
	scrollable = false,
	className = '',
	contentClassName = '',
	scrollTopCondition = false,
	hFull,
	...props
}: ContainerProps) => {
	const [topFadeOpacity, setTopFadeOpacity] = useState(0);
	const [bottomFadeOpacity, setBottomFadeOpacity] = useState(0);
	const scrollRef = useRef<any>(null);
	const dragScrollRef = useDragScroll();
	const scrollContainerRef = scrollable ? dragScrollRef : scrollRef;

	const computeFade = useCallback(() => {
		if (!scrollable || !dragScrollRef.current) return;
		const { scrollTop, scrollHeight, clientHeight } = dragScrollRef.current;
		const maxScrollTop = scrollHeight - clientHeight;

		setTopFadeOpacity(Math.min(scrollTop / 20, 1));
		setBottomFadeOpacity(Math.min((maxScrollTop - scrollTop) / 20, 1));
	}, [dragScrollRef, scrollable]);

	useEffect(() => {
		if (scrollTopCondition && scrollRef.current) {
			scrollRef.current.scrollTo(0, 0);
		}
	}, [scrollTopCondition]);

	useEffect(() => {
		computeFade(); // Initialize the fade visibility on mount
	}, [children, computeFade]);

	return (
		<Card {...props} className={cn('relative text-card-foreground', className)}>
			{scrollable && (
				<>
					<div
						style={{ opacity: topFadeOpacity }}
						className="pointer-events-none absolute left-0 top-0 z-10 h-10 w-full rounded-t-md bg-linear-to-b from-card to-transparent"
					/>
					<div
						style={{ opacity: bottomFadeOpacity }}
						className="pointer-events-none absolute bottom-0 left-0 z-10 h-10 w-full rounded-b-md bg-linear-to-b from-transparent to-card"
					/>
				</>
			)}
			<div
				className={cn('relative w-full min-w-0 grow', hFull && 'h-full', scrollable && 'overflow-y-auto')}
				ref={scrollContainerRef}
				onScroll={computeFade}
			>
				<CardContent className={cn('h-full w-full min-w-0 px-4', scrollable && 'absolute', contentClassName)}>{children}</CardContent>
			</div>
		</Card>
	);
};

Container.displayName = 'Container';
